import { TPA_STREAM_CONNECT_TOKEN_STORAGE_KEY } from 'config/tpaStreamStorageKeys';
import { clearFlowState } from 'pages/organizationFlow/flowStorage';
import { clearConfirmHsaAccountFrontendComplete } from 'pages/payoutSetup/confirmHsaAccountProgressStorage';
import { ADMIN_BASE_PATH } from 'routes/constants/adminPaths';
import { resetAdminPersistedJourneyState } from 'store/adminJourneyReset';
import { persistor, store } from 'store/store';

import { clearAdminOrganizationResumeStorage } from 'utils/adminOrganizationResumeStorage';
import { clearFindExpensesCompletedLock } from 'utils/findExpensesCompletedLock';
import { clearOnboardingJourneyState } from 'utils/onboardingFlowStorage';

import { clearTpaStreamConnectToken } from 'services/api/tpaStreamConnectTokenMemory';

import { persistAdminSessionExpiredFlash } from './adminSessionExpiredFlash';
import {
  clearAuthTokens,
  clearChallengeSession,
  getAdminAccessToken,
  getAdminIdToken,
  getUserAccessToken,
  getUserIdToken,
  redirectToLogin,
} from './authTokenStorage';
import { persistEmployeeSessionExpiredFlash } from './employeeSessionExpiredFlash';
import { getSessionRealm, type SessionRealm } from './sessionRealm';

const END_USER_TOAST_SEEN_KEY_PREFIX = 'endUserConnectedToastSeen';
const TPA_STREAM_CONTEXT_SESSION_KEY = 'tpaStreamSessionContext';

/** Prevents concurrent 401/403 responses from double-purging / double-redirecting. */
let sessionExpiryHandlingPromise: Promise<void> | null = null;

function clearEndUserLocalStorageMirrors(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // SEC-LOW-01: clear in-memory connect token + any legacy localStorage copy.
    clearTpaStreamConnectToken();

    if (typeof window.localStorage?.removeItem === 'function') {
      window.localStorage.removeItem('euId');
      window.localStorage.removeItem('pendingExpenseId');
      window.localStorage.removeItem(TPA_STREAM_CONNECT_TOKEN_STORAGE_KEY);
      // Legacy euId-keyed toast flags — no longer written; purge leftovers on logout.
      const keys: string[] = [];
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i);
        if (key?.startsWith(END_USER_TOAST_SEEN_KEY_PREFIX)) {
          keys.push(key);
        }
      }

      keys.forEach(k => window.localStorage.removeItem(k));
    }

    // TPA Stream context is stored in sessionStorage by tpaStreamApi.
    if (typeof window.sessionStorage?.removeItem === 'function') {
      window.sessionStorage.removeItem(TPA_STREAM_CONTEXT_SESSION_KEY);
    }

    // Boolean connect-celebration lock only — never identifiers or clinical data.
    clearFindExpensesCompletedLock();

    // Onboarding prefill may contain email / phone / name — clear on session end (SEC-MED-01).
    clearOnboardingJourneyState();
    clearConfirmHsaAccountFrontendComplete();
  } catch {
    // Ignore storage errors during cleanup.
  }
}

function isAdminPathname(pathname: string): boolean {
  return pathname === ADMIN_BASE_PATH || pathname.startsWith(`${ADMIN_BASE_PATH}/`);
}

/**
 * Resolve which login screen to use after logout/expiry.
 * Captured before token purge. Does not persist realm after redirect.
 */
export function resolveRealmForTermination(
  realmOverride?: SessionRealm | null
): SessionRealm | null {
  if (realmOverride === 'admin' || realmOverride === 'user') {
    return realmOverride;
  }

  const storedRealm = getSessionRealm();
  if (storedRealm) {
    return storedRealm;
  }

  const hasAdminToken = Boolean(getAdminIdToken() ?? getAdminAccessToken());
  const hasUserToken = Boolean(getUserIdToken() ?? getUserAccessToken());

  if (hasAdminToken !== hasUserToken) {
    return hasAdminToken ? 'admin' : 'user';
  }

  if (typeof window !== 'undefined' && isAdminPathname(window.location.pathname)) {
    return 'admin';
  }

  if (hasUserToken || hasAdminToken) {
    return 'user';
  }

  return null;
}

function redirectAfterTermination(realm: SessionRealm | null): void {
  // Realm is resolved before purge; null means no admin signal → user login.
  redirectToLogin(realm === 'admin' ? 'admin' : 'user');
}

export async function terminateSession(realmOverride?: SessionRealm | null): Promise<void> {
  // Resolve before purge/clearAuthTokens (those clear spence_session_realm + tokens).
  const realm = resolveRealmForTermination(realmOverride);

  try {
    // Clear in-memory auth/PHI slices, then drop remaining persisted onboarding state.
    store.dispatch(resetAdminPersistedJourneyState());
    await persistor.flush();
    // SEC-MED-03: remove persist:spence-root from browser storage.
    await persistor.purge();
  } catch {
    // Persist flush/purge must not block token cleanup or login redirect.
  }

  clearFlowState();
  if (realm === 'admin') {
    clearAdminOrganizationResumeStorage();
  }

  clearEndUserLocalStorageMirrors();
  clearAuthTokens();
  clearChallengeSession();

  if (typeof window === 'undefined') {
    return;
  }

  // Defense in depth: ensure the redux-persist root key is gone even if purge no-ops.
  try {
    window.localStorage?.removeItem('persist:spence-root');
  } catch {
    // Ignore storage errors during cleanup.
  }

  redirectAfterTermination(realm);
}

/**
 * SEC-MED-03: API 401 / refresh failure — same purge path as manual logout.
 * Admin and Employee flows each flash a one-shot toast on their login screen.
 * Concurrent expiry responses share a single terminate + toast.
 */
export function handleSessionExpired(): void {
  if (sessionExpiryHandlingPromise) {
    return;
  }

  const realm = resolveRealmForTermination();
  if (realm === 'admin') {
    persistAdminSessionExpiredFlash();
  } else {
    // Employee login is the default when realm cannot be resolved as admin.
    persistEmployeeSessionExpiredFlash();
  }

  sessionExpiryHandlingPromise = terminateSession(realm).finally(() => {
    sessionExpiryHandlingPromise = null;
  });
}

/** Test-only: reset the once-only guard between Vitest cases. */
export function resetSessionExpiryHandlingForTests(): void {
  sessionExpiryHandlingPromise = null;
}
